import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
  InputAdornment,
  Card,
  CardContent,
  Box,
  Alert,
  Collapse
} from '@mui/material';
import {
  Person,
  Email,
  Public,
} from '@mui/icons-material';
import { BandRecommendationFormData, EuropeanCountry, IrishCounty } from '../../types/BandRecommendations';

interface RecommenderInfoProps {
  formData: BandRecommendationFormData;
  setFormData: React.Dispatch<React.SetStateAction<BandRecommendationFormData>>;
}

const RecommenderInfo: React.FC<RecommenderInfoProps> = ({ formData, setFormData }) => {
  const handleTextFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
      ...(name === 'recommenderCountry' && value !== 'Ireland' && { county: '' }),
    }));
  };

  const isIreland = formData.recommenderCountry === 'Ireland';

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        Enter the name and email of the artist who will own this page. When they sign up using this
        email address, this band page will automatically appear in their dashboard.
      </Alert>

      {/* Owner Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person color="primary" />
            Page Owner Details
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="recommenderName"
                label="Owner Name"
                value={formData.recommenderName || ''}
                onChange={handleTextFieldChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: 'action.active' }} />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ autoComplete: 'off' }}
                placeholder="Artist or band leader name"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="recommenderEmail"
                label="Owner Email"
                type="email"
                value={formData.recommenderEmail || ''}
                onChange={handleTextFieldChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'action.active' }} />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ autoComplete: 'off' }}
                placeholder="artist@example.com"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Location */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Public color="primary" />
            Location
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={isIreland ? 6 : 12}>
              <FormControl fullWidth>
                <InputLabel id="country-label">Country</InputLabel>
                <Select
                  labelId="country-label"
                  name="recommenderCountry"
                  value={formData.recommenderCountry || ''}
                  onChange={handleSelectChange}
                  label="Country"
                >
                  {Object.values(EuropeanCountry).map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Collapse in={isIreland} sx={{ width: '100%' }}>
              <Grid item xs={12} sm={6} sx={{ pl: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="county-label">County</InputLabel>
                  <Select
                    labelId="county-label"
                    name="county"
                    value={formData.county || ''}
                    onChange={handleSelectChange}
                    label="County"
                  >
                    {Object.values(IrishCounty).map((county) => (
                      <MenuItem key={county} value={county}>
                        {county}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Collapse>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RecommenderInfo;